import _ from 'lodash'
import { useCallback } from 'react'
import { useTranslate } from 'src/localization/Trns'

interface ParseBackendErrorsReturn<E extends string> {
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<E>
}
export interface BackendFieldError<E> {
  field?: E
  error: string
  invalidValue?: string
}
/* eslint-disable no-unused-vars */
export type ErrorDetailMap<E extends string> = Record<string, string[]>
export interface BackendErrorPayload<E> {
  error: string
  errorCode?: string
  details?: Array<BackendFieldError<E>>
}
export const parseBackendErrors = <E extends string>(
  payload: BackendErrorPayload<E>,
): ParseBackendErrorsReturn<E> => {
  const topLevelError = _.get(payload, 'error', undefined)
  const errorDetailMap: Record<string, string[]> | undefined =
    _.get(payload, 'details', undefined) !== undefined
      ? payload.details?.reduce(
          (acc: Record<string, string[]> | undefined, curr) => {
            if (curr.field !== undefined) {
              const newAcc = acc ? { ...acc } : {}
              const list = acc?.[curr.field]
              if (list === undefined) {
                newAcc[curr.field] = [curr.error]
              } else {
                newAcc[curr.field] = list.concat([curr.error])
              }
              return newAcc
            } else {
              return acc
            }
          },
          undefined,
        )
      : undefined
  return { topLevelError, errorDetailMap }
}

export const useNormalizeBackendError = (): ((
  error: any,
) => BackendErrorPayload<string>) => {
  const translate = useTranslate()

  return useCallback(
    (error: any): BackendErrorPayload<string> => {
      // Handle Axios errors more robustly
      if (error && typeof error === 'object' && 'response' in error) {
        const responseData = error.response?.data

        // Check if response.data is already a BackendErrorPayload
        if (
          responseData &&
          typeof responseData === 'object' &&
          'error' in responseData
        ) {
          return responseData as BackendErrorPayload<string>
        }
      }

      // Handle HTTP status messages
      if (error.response?.statusText) {
        return { error: error.response.statusText }
      }

      // Handle already normalized errors
      if (error && typeof error === 'object' && 'error' in error) {
        return error as BackendErrorPayload<string>
      }

      // Convert Error objects
      if (error instanceof Error) {
        return { error: error.message }
      }

      // Convert strings
      if (typeof error === 'string') {
        return { error }
      }

      return {
        error: translate(
          'Sorry, something went wrong. Please try again later.',
        ),
      }
    },
    [translate],
  )
}

export const errorPath = (segments: Array<string | number>): string | number =>
  segments.reduce(
    (acc: string, curr, i) =>
      i === 0
        ? `${curr}`
        : typeof curr === 'number'
          ? `${acc}[${curr}]`
          : `${acc}.${curr}`,
    '',
  )
